<template>
  <b-container style="max-width:1100px;">
    <b-card>
      <h3>Manage Consumer</h3>
      <br />
      <b-card-text>
        <b-input-group prepend="Key">
          <b-form-input autocomplete="off" v-model="form.key" type="text" @keyup.enter="findConsumer"
            @input="handleKeyInput"></b-form-input>
          <b-input-group-append>
            <b-button variant="success" @click="findConsumer">Find
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <div v-if="invalid" class="text-danger mt-3">
          <b-icon icon="exclamation-circle" aria-hidden="true"></b-icon>
          Key field cannot be blank
        </div>
      </b-card-text>

      <!-- Multiple matches list -->
      <div v-if="matches.length > 0" class="mb-3">
        <b>Found {{ matches.length }} match{{ matches.length > 1 ? 'es' : '' }}:</b>
        <b-list-group class="mt-2">
          <b-list-group-item
            v-for="match in matches"
            :key="match.id"
            button
            @click="selectConsumer(match)"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>{{ match.key }}</strong>
              <span class="text-muted ml-2">{{ match.name }}</span>
            </div>
            <b-icon icon="box-arrow-in-right" class="text-muted"></b-icon>
          </b-list-group-item>
        </b-list-group>

        <div class="mt-3">
          <b>Or create a new consumer with key "{{ form.key }}":</b>
          <b-input-group prepend="Name" class="mt-2">
            <b-form-input v-model="form.name" type="text" placeholder="Enter consumer name"></b-form-input>
            <b-input-group-append>
              <b-button variant="success" @click="createConsumer">Create
                <b-spinner v-if="isSending" class="m-1" label="Spinning" small />
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="nameValidation">
            Name field cannot be blank
          </b-form-invalid-feedback>
        </div>
      </div>

      <div v-if="consumer">
        <div v-if="consumer.found_consumer_by_key">
          <b>Consumer:</b>
          <ul class="pl-0 pt-2">
            <li style="list-style-type:none; margin-top: 10px;">
              <b-input-group prepend="Key" size="sm">
                <b-form-input v-model="form.edit_key" type="text" @input="updated = false; updateError = null"></b-form-input>
              </b-input-group>
              <small class="text-muted">Changing the key will break existing LTI 1.1 launches from the school's LMS.</small>
            </li>
            <li style="list-style-type:none; margin-top: 10px;">
              <b-input-group prepend="Name" size="sm">
                <b-form-input v-model="form.edit_name" type="text" @input="updated = false; updateError = null"></b-form-input>
              </b-input-group>
            </li>
            <li style="list-style-type:none; margin-top: 10px;">Secret: {{ consumer.found_consumer_by_key.secret }}</li>
            <li style="list-style-type:none; display: flex; align-items: center;">
              <span class="mr-2">Accepts Payment: </span>
              <b-form-checkbox v-model="form.accepts_payment" switch @change="validateToggle('accepts_payment')">
                {{ form.accepts_payment ? 'Enabled' : 'Disabled' }}
              </b-form-checkbox>
            </li>
            <li style="list-style-type:none; display: flex; align-items: center;">
              <span class="mr-2">Accepts Access Code: </span>
              <b-form-checkbox v-model="form.accepts_access_code" switch
                @change="validateToggle('accepts_access_code')">
                {{ form.accepts_access_code ? 'Enabled' : 'Disabled' }}
              </b-form-checkbox>
            </li>
            <li style="list-style-type:none; display: flex; align-items: center;">
              <span class="mr-2">Shows Teacher Resources: </span>
              <b-form-checkbox v-model="form.shows_teacher_resources" switch
                @change="updated = false; updateError = null">
                {{ form.shows_teacher_resources ? 'Enabled' : 'Disabled' }}
              </b-form-checkbox>
            </li>
            <li style="list-style-type:none;">
              <div style="display: flex; align-items: center;">
                <span class="mr-2">ASL Dictionary (WTS): </span>
                <b-form-checkbox v-model="form.allows_wts" switch
                  @change="updated = false; updateError = null">
                  {{ form.allows_wts ? 'Enabled' : 'Disabled' }}
                </b-form-checkbox>
              </div>
              <small class="text-muted">
                Governs only the courses set to "Inherit from School" — a course with its own
                WTS setting keeps it. Set per course under Activate 1.1 Course.
              </small>
            </li>
            <li style="list-style-type:none; margin-top: 10px;">
              <b-input-group prepend="Email Domain" size="sm">
                <b-form-input v-model="form.email_domain" type="text" placeholder="e.g., .edu or stanford.edu,berkeley.edu" @input="updated = false; updateError = null"></b-form-input>
              </b-input-group>
              <small class="text-muted">Restrict Direct Join to specific email domains (comma-separated)</small>
            </li>
            <li style="list-style-type:none; margin-top: 10px;">
              <b-input-group prepend="Auto-Activate Product" size="sm">
                <b-form-select v-model="form.product_id" :options="productOptions" @change="updated = false; updateError = null"></b-form-select>
              </b-input-group>
              <small class="text-muted">New courses for this consumer will be auto-activated with this product</small>
            </li>
            <li style="list-style-type:none; margin-top: 10px;">
              <b-input-group prepend="Auto-Enroll Access Code" size="sm">
                <b-form-input v-model.trim="form.auto_enroll_coupon_code" type="text"
                  placeholder="e.g., SPRING25" @input="updated = false; updateError = null"></b-form-input>
              </b-input-group>
              <small v-if="auto_enroll_summary" class="text-muted d-block">
                {{ auto_enroll_summary.product_code }} · {{ auto_enroll_summary.usage }} / {{ auto_enroll_summary.max_usage }}
                {{ auto_enroll_summary.counts_unique_emails ? 'unique-email seats used' : 'seats used' }}
              </small>
              <small v-if="autoEnrollMismatch" class="text-warning d-block">
                ⚠️ This code is for {{ auto_enroll_summary.product_code }}, but Auto-Activate Product differs.
                Students won't be auto-enrolled until the products match.
              </small>
              <!-- TWA-275: both switches effectively on at school level. Deliberate for
                   manually rostered premium KSS schools (TWA-283), so this names the
                   consequence rather than flagging an error. -->
              <!-- A plain div with v-if, not <b-alert>: b-alert kept a stale warning on
                   screen when the condition went false (see Course.vue). -->
              <div v-if="wtsAutoEnrollBoth" class="alert alert-warning mt-2 mb-1 py-2 px-2" role="alert">
                <small>
                  <strong>ASL Dictionary is on and an auto-enroll code is set for this school.</strong>
                  Students reach the dictionary without signing up, so they will not be
                  enrolled, no seats will be claimed, and no grade is passed back —
                  <!-- The school switch governs only courses set to inherit. With none today the
                       warning still stands, because lti_courses.allows_wts defaults to NULL: the
                       next course created under this key inherits. Saying "0 courses" there read
                       as a bug and undercut the warning, so name the forward-looking case. -->
                  <template v-if="inheritingWtsCourseCount > 0">for
                    <strong>{{ inheritingWtsCourseCount }}</strong>
                    course{{ inheritingWtsCourseCount === 1 ? '' : 's' }} set to "Inherit from School",
                    plus any course with WTS enabled on itself.</template>
                  <template v-else>for any course added under this key from now on, since new
                    courses inherit this setting by default, plus any existing course with WTS
                    enabled on itself.</template>
                  Fine if this school is manually rostered; not fine if students are meant
                  to complete the Sign Up module.
                </small>
              </div>
              <small class="text-muted d-block">
                Binding here creates ONE seat pool shared by every matching course under this key.
                For a shared, district, or catch-all key (twa_key, ccisd_key, tvusd_key, …), bind at the COURSE level instead — Activate 1.1 Course — so each licensed course draws its own pool. A course-level code overrides this one.
                A bound code switches to <b>auto-enroll mode</b>: students enroll with the code and are blocked at cap; payment/access-code settings are unused.
              </small>
            </li>
            <li style="list-style-type: none; margin-top: 10px;">
              <b-button variant="success" @click="updateConsumer" :disabled="!canUpdate">
                Update
                <b-spinner v-if="isUpdating" class="m-1" label="Spinning" small />
              </b-button>
              <!-- The save's answer sits right under the button, above the course table. -->
              <div v-if="updated" class="text-success mt-2">
                <b-icon icon="check-circle" aria-hidden="true"></b-icon>
                Consumer Updated Successfully
              </div>
              <div v-if="updateError" class="text-danger mt-2">
                <b-icon icon="exclamation-circle" aria-hidden="true"></b-icon>
                {{ updateError }}
              </div>
              <!-- TWA-944: current courses that inherit the code are on another product; save only on an explicit tick. -->
              <b-form-checkbox v-if="refusalCodes.includes('current_courses_mismatch')" v-model="allowMismatchedCourses" class="mt-2">
                These courses use their own access codes (save anyway)
              </b-form-checkbox>
            </li>
          </ul>
          <!-- Courses -->
          <div v-if="allCourses.length > 0" class="mt-3">
            <div class="d-flex justify-content-between align-items-center">
              <b>Courses ({{ allCourses.length }}<span v-if="totalCourses > allCourses.length"> of {{ totalCourses }}</span>):</b>
              <div>
                <b-dropdown size="sm" variant="outline-secondary" text="Columns" class="mr-1">
                  <b-dropdown-form v-for="col in columnDefs" :key="col.key">
                    <b-form-checkbox v-model="visibleColumns" :value="col.key">{{ col.label }}</b-form-checkbox>
                  </b-dropdown-form>
                </b-dropdown>
                <b-button size="sm" variant="outline-secondary" @click="exportCsv">
                  <b-icon icon="download" class="mr-1"></b-icon>Export CSV
                </b-button>
              </div>
            </div>
            <b-table-simple small bordered hover class="mt-2" style="font-size: 0.85em;">
              <b-thead>
                <b-tr>
                  <b-th v-for="col in activeColumns" :key="col.key">{{ col.label }}</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="course in displayedCourses" :key="course.id">
                  <b-td v-for="col in activeColumns" :key="col.key">
                    <template v-if="col.key === 'name'">
                      <router-link :to="`/course/${course.context_id}`">{{ cellValue(course, 'name') }}</router-link>
                    </template>
                    <template v-else-if="col.key === 'join_link' || col.key === 'course_id'" class="text-nowrap">
                      <span :title="course.context_id">{{ truncateId(course.context_id) }}</span>
                      <b-icon :icon="copiedId === course.id ? 'check-circle' : 'clipboard'" class="ml-1" :class="copiedId === course.id ? 'text-success' : 'text-muted'" style="cursor: pointer;" @click="copyToClipboard(course.context_id, course.id)" :title="'Copy ' + course.context_id"></b-icon>
                    </template>
                    <template v-else-if="col.key === 'products'">
                      <span v-if="course.products.length" class="text-success">{{ cellValue(course, 'products') }}</span>
                      <span v-else class="text-muted">None</span>
                    </template>
                    <template v-else-if="col.key === 'expires'">
                      <span v-if="course.expires_at" :class="isExpired(course.expires_at) ? 'text-danger' : ''">{{ cellValue(course, 'expires') }}</span>
                      <span v-else class="text-muted">—</span>
                    </template>
                    <template v-else>
                      {{ cellValue(course, col.key) || '—' }}
                    </template>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="d-flex justify-content-between">
              <small v-if="allCourses.some(c => c.counts_unique_emails)" class="text-muted">* Counts unique emails</small>
              <small v-else></small>
              <a v-if="allCourses.length > 10" href="#" @click.prevent="showAllCourses = !showAllCourses" class="small">
                {{ showAllCourses ? 'Show recent 10' : `Show all ${allCourses.length} courses` }}
              </a>
            </div>
          </div>
          <div v-else-if="consumer.found_consumer_by_key && !isSending" class="mt-3 text-muted">
            <small>No courses found for this consumer.</small>
          </div>
        </div>
        <div v-if="consumer.message">
          <div class="text-warning mb-3">
            <b-icon icon="exclamation-circle" aria-hidden="true"></b-icon>
            Consumer with key "{{ searchKey }}" not found
          </div>
          <b>Would you like to create a new consumer with this key?</b>
          <b-card-text class="mt-2">
            <b-input-group prepend="Name">
              <b-form-input v-model="form.name" type="text" placeholder="Enter consumer name"></b-form-input>
              <b-input-group-append>
                <b-button variant="success" @click="createConsumer">
                  Create
                  <b-spinner v-if="isSending" class="m-1" label="Spinning" small />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback :state="nameValidation">
              Name field cannot be blank
            </b-form-invalid-feedback>
          </b-card-text>
        </div>
        <!-- A create (partial-match list or not-found block) has no Update button: its error shows here. -->
        <div v-if="updateError && !consumer.found_consumer_by_key" class="text-danger mb-3">
          <b-icon icon="exclamation-circle" aria-hidden="true"></b-icon>
          {{ updateError }}
        </div>
      </div>

      <b-card-text>

      </b-card-text>
    </b-card>

    <!-- Confirm Key Rename Modal -->
    <b-modal
      v-model="showRenameModal"
      title="Confirm consumer key rename"
      @hidden="onRenameCancel">
      <p class="my-2">
        Changing the consumer key from <strong>{{ initial_values.key }}</strong>
        to <strong>{{ form.edit_key }}</strong> will break existing LTI 1.1 launches
        from the school's LMS until they update their tool configuration.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" @click="onRenameCancel">Cancel</b-button>
        <b-button variant="danger" @click="onRenameConfirm">Confirm rename</b-button>
      </template>
    </b-modal>

    <!-- Confirm school-level WTS change (TWA-256 guardrail) -->
    <b-modal
      v-model="showWtsModal"
      title="Confirm ASL Dictionary change for the whole school"
      @hidden="onWtsCancel">
      <p v-if="inheritingWtsCourseCount > 0" class="my-2">
        Turning the ASL Dictionary <strong>{{ form.allows_wts ? 'on' : 'off' }}</strong> for
        <strong>{{ initial_values.key }}</strong> will change dictionary access for
        <strong>{{ inheritingWtsCourseCount }}</strong>
        existing course{{ inheritingWtsCourseCount === 1 ? '' : 's' }} set to
        "Inherit from School".
      </p>
      <p v-else class="my-2">
        No existing course under <strong>{{ initial_values.key }}</strong> is set to
        "Inherit from School", so this changes nothing today. It will apply to courses
        created from now on, and to any course an admin switches to Inherit.
      </p>
      <p class="my-2 text-muted">
        Courses with their own WTS setting are unaffected. On a shared or district key
        this count grows as new courses launch, so it may be far larger next time.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" @click="onWtsCancel">Cancel</b-button>
        <b-button variant="danger" @click="onWtsConfirm">
          <span v-if="inheritingWtsCourseCount > 0">
            Change {{ inheritingWtsCourseCount }} course{{ inheritingWtsCourseCount === 1 ? '' : 's' }}
          </span>
          <span v-else>Turn {{ form.allows_wts ? 'on' : 'off' }} anyway</span>
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import {
  findConsumer, checkConsumerKey, createConsumer, updateConsumer, getProducts
} from "../../utils/twa-api"
import { formatDate } from "../../utils/date"

export default {
  data() {
    return {
      form: {
        key: this.$route.params.key || this.$route.query.key || '',
        name: '',
        edit_key: '',
        edit_name: '',
        accepts_payment: true,
        accepts_access_code: true,
        shows_teacher_resources: true,
        allows_wts: false,
        email_domain: '',
        product_id: null,
        auto_enroll_coupon_code: ''
      },
      searchKey: '',
      isSending: false,
      isUpdating: false,
      isProcessing: false,
      consumer: null,
      invalid: false,
      newConsumer: false,
      updated: true,
      initial_values: { key: '', name: '', accepts_payment: false, accepts_access_code: false, shows_teacher_resources: true, allows_wts: false, email_domain: '', product_id: null, auto_enroll_coupon_code: '' },
      auto_enroll_bound_product_id: null,
      auto_enroll_summary: null,
      nameValidation: false,
      updateError: null,
      refusalCodes: [],
      allowMismatchedCourses: false,
      matches: [],
      allProducts: [],
      allCourses: [],
      totalCourses: 0,
      showAllCourses: false,
      copiedId: null,
      visibleColumns: ['name', 'teacher_first', 'teacher_last', 'email', 'dates', 'join_link'],
      showRenameModal: false,
      renameResolver: null,
      showWtsModal: false,
      wtsResolver: null,
      // Courses under this consumer sitting at "Inherit from School" — the blast
      // radius of the WTS switch (TWA-256). Re-fetched right before the confirm.
      inheritingWtsCourseCount: 0
    }
  },
  watch: {
    // TWA-944: a confirmation answers the refusal for one code and product; changing either retires it.
    'form.auto_enroll_coupon_code'() { this.refusalCodes = []; this.allowMismatchedCourses = false },
    'form.product_id'() { this.refusalCodes = []; this.allowMismatchedCourses = false }
  },
  computed: {
    canUpdate() {
      const atLeastOneEnabled = this.form.accepts_payment || this.form.accepts_access_code;
      const editKey = (this.form.edit_key || '').trim();
      const editName = (this.form.edit_name || '').trim();
      const keyAndNamePresent = !!editKey && !!editName;

      const valuesChanged =
        this.form.accepts_payment !== this.initial_values.accepts_payment ||
        this.form.accepts_access_code !== this.initial_values.accepts_access_code ||
        this.form.shows_teacher_resources !== this.initial_values.shows_teacher_resources ||
        this.form.allows_wts !== this.initial_values.allows_wts ||
        this.form.email_domain !== this.initial_values.email_domain ||
        this.form.product_id !== this.initial_values.product_id ||
        this.form.auto_enroll_coupon_code !== this.initial_values.auto_enroll_coupon_code ||
        editKey !== this.initial_values.key ||
        editName !== this.initial_values.name;

      return valuesChanged && atLeastOneEnabled && keyAndNamePresent;
    },
    // TWA-275: both switches on at school level. Reads the pending form values so the
    // note appears as the second one is flipped, not only after saving.
    //
    // Gated on a change from the saved row (hand-off point 2): an existing both-set
    // school is usually deliberate after the premium-KSS sweep (TWA-283), so opening
    // its page shows no badge.
    wtsAutoEnrollBoth() {
      const code = (this.form.auto_enroll_coupon_code || '').trim()
      if (!this.form.allows_wts || !code) return false

      return this.form.allows_wts !== this.initial_values.allows_wts ||
        code !== (this.initial_values.auto_enroll_coupon_code || '')
    },
    autoEnrollMismatch() {
      return this.auto_enroll_summary &&
        this.form.product_id &&
        this.auto_enroll_bound_product_id &&
        this.auto_enroll_bound_product_id !== this.form.product_id
    },
    displayedCourses() {
      return this.showAllCourses ? this.allCourses : this.allCourses.slice(0, 10)
    },
    productOptions() {
      const options = [{ value: null, text: 'None (no auto-activate)' }]
      this.allProducts
        .filter(p => p.name.toLowerCase() !== 'zfree')
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(p => options.push({ value: p.id, text: `${p.name} (${p.product_code})` }))
      return options
    },
    columnDefs() {
      return [
        { key: 'name', label: 'Course Name', default: true },
        { key: 'teacher_first', label: 'Teacher: First Name', default: true },
        { key: 'teacher_last', label: 'Teacher: Last Name', default: true },
        { key: 'email', label: 'Email', default: true },
        { key: 'dates', label: 'Created / Activated', default: true },
        { key: 'join_link', label: 'Context ID', default: true },
        { key: 'course_id', label: 'Course ID', default: false },
        { key: 'canvas_id', label: 'Canvas ID', default: false },
        { key: 'domain', label: 'Domain', default: false },
        { key: 'products', label: 'Products', default: false },
        { key: 'source', label: 'Source', default: false },
        { key: 'enrollments', label: 'Enrollments', default: false },
        { key: 'expires', label: 'Expires', default: false }
      ]
    },
    activeColumns() {
      return this.columnDefs.filter(c => this.visibleColumns.includes(c.key))
    }
  },
  async mounted() {
    try {
      this.allProducts = await getProducts()
    } catch (e) {
      console.error('Failed to load products', e)
    }
    // Deep link (?key= / /manage_consumer/:key — e.g. from the Auto-Enroll
    // (School) readout or the School block): the key is already prefilled into
    // form.key by data(); run the lookup so the consumer is on screen on arrival.
    if (this.form.key) {
      await this.findConsumer()
    }
  },
  methods: {
    handleLoginEvent(data) {
      this.isAuthenticated = data.loggedIn
      this.profile = data.profile
    },
    validateToggle(field) {
      this.updated = false;
      this.updateError = null;
      if (!this.form.accepts_payment && !this.form.accepts_access_code) {
        this.form[field] = true
      }
    },
    async findConsumer() {
      this.nameValidation = true
      this.updated = false
      this.updateError = null
      this.matches = []
      this.allCourses = []
      this.totalCourses = 0
      this.showAllCourses = false
      this.isSending = true
      // Defensive: ensure products are loaded before the Auto-Activate Product
      // dropdown renders (mounted-time fetch may not have resolved yet).
      if (this.allProducts.length === 0) {
        try { this.allProducts = await getProducts() } catch (e) { console.error('Failed to load products', e) }
      }
      if (this.form.key) {
        this.searchKey = this.form.key
        this.consumer = await findConsumer(this.form.key)
        if (this.consumer.found_consumer_by_key) {
          this.allCourses = this.consumer.courses || []
          this.totalCourses = this.consumer.total_courses || this.allCourses.length
          this.inheritingWtsCourseCount = this.consumer.inheriting_wts_course_count || 0
          this.selectConsumer(this.consumer.found_consumer_by_key)
        } else if (this.consumer.matches) {
          this.matches = this.consumer.matches
        }
        this.invalid = false
      } else {
        this.invalid = true
      }
      this.isSending = false
    },
    selectConsumer(consumer) {
      this.matches = []
      // A message from the previous step (a failed create, an earlier save) never carries over to this consumer.
      this.updateError = null
      this.updated = false
      this.refusalCodes = []
      this.allowMismatchedCourses = false
      this.form.key = consumer.key
      this.form.edit_key = consumer.key
      this.form.edit_name = consumer.name
      this.form.accepts_payment = consumer.accepts_payment
      this.form.accepts_access_code = consumer.accepts_access_code
      this.form.shows_teacher_resources = consumer.shows_teacher_resources !== false
      // allows_wts defaults OFF (opposite of shows_teacher_resources), so an
      // absent field must read as false, not true.
      this.form.allows_wts = consumer.allows_wts === true
      this.form.email_domain = consumer.email_domain || ''
      this.form.product_id = consumer.product_id || null
      this.form.auto_enroll_coupon_code = consumer.auto_enroll_coupon_code || ''
      this.auto_enroll_bound_product_id = consumer.auto_enroll_product_id || null
      this.auto_enroll_summary = consumer.auto_enroll_coupon_code ? {
        product_code: consumer.auto_enroll_product_code,
        usage: consumer.auto_enroll_usage,
        max_usage: consumer.auto_enroll_max_usage,
        counts_unique_emails: consumer.auto_enroll_counts_unique_emails
      } : null
      this.initial_values = {
        key: consumer.key,
        name: consumer.name,
        accepts_payment: consumer.accepts_payment,
        accepts_access_code: consumer.accepts_access_code,
        shows_teacher_resources: consumer.shows_teacher_resources !== false,
        allows_wts: consumer.allows_wts === true,
        email_domain: consumer.email_domain || '',
        product_id: consumer.product_id || null,
        auto_enroll_coupon_code: consumer.auto_enroll_coupon_code || ''
      }
      this.consumer = { found_consumer_by_key: consumer }
    },
    async createConsumer() {
      if (!this.form.name) {
        this.nameValidation = false
        return
      }
      this.nameValidation = true
      this.updated = false;
      this.updateError = null
      this.isSending = true
      if (this.form.key) {
        this.searchKey = this.form.key
        try {
          const result = await createConsumer(this.form.key, this.form.name)
          if (result.created_consumer) {
            this.selectConsumer(result.created_consumer)
            this.form.name = ''
          }
        } catch (error) {
          // Backend returns 422 + errors[] for a key/name already in use
          this.updateError = error.response?.data?.errors?.join(', ') || error.message || 'Failed to create consumer'
        }
        this.invalid = false
      } else {
        this.invalid = true
      }
      this.isSending = false
    },
    async updateConsumer() {
      if (!this.form.key) {
        this.invalid = true
        return
      }
      const keyChanged = this.form.edit_key !== this.initial_values.key
      if (keyChanged) {
        // Block upfront if the new key is already taken, before warning about LTI breakage
        this.isUpdating = true
        let check
        try {
          check = await checkConsumerKey(this.form.edit_key, this.initial_values.key)
        } catch (error) {
          this.updateError = error.message || 'Failed to check key availability'
          this.isUpdating = false
          return
        }
        this.isUpdating = false
        if (check.exists) {
          this.updateError = `Key '${this.form.edit_key}' is already in use`
          this.form.edit_key = this.initial_values.key
          return
        }
        const confirmed = await this.confirmKeyRename()
        if (!confirmed) return
      }
      // TWA-256 guardrail: one flip here moves every inheriting course under the
      // key, so confirm against a fresh count. Re-fetching covers both a stale
      // page (courses launch between load and save) and arrival via the
      // partial-match list, which carries no count.
      if (this.form.allows_wts !== this.initial_values.allows_wts) {
        this.isUpdating = true
        try {
          const fresh = await findConsumer(this.initial_values.key)
          this.inheritingWtsCourseCount = fresh.inheriting_wts_course_count || 0
        } catch (error) {
          this.updateError = error.message || 'Failed to check how many courses this would affect'
          this.isUpdating = false
          return
        }
        this.isUpdating = false
        const confirmed = await this.confirmWtsChange()
        if (!confirmed) {
          this.form.allows_wts = this.initial_values.allows_wts
          return
        }
      }
      this.isUpdating = true
      try {
        this.consumer = await updateConsumer(
          this.initial_values.key,
          this.form.accepts_payment,
          this.form.accepts_access_code,
          this.form.email_domain || null,
          this.form.product_id,
          this.form.edit_key,
          this.form.edit_name,
          this.form.shows_teacher_resources,
          this.form.auto_enroll_coupon_code,
          this.form.allows_wts,
          this.allowMismatchedCourses
        )
        if (this.consumer.updated_consumer) {
          this.inheritingWtsCourseCount = this.consumer.inheriting_wts_course_count || 0
          this.selectConsumer(this.consumer.updated_consumer)
          this.updated = true
          this.updateError = null
        }
      } catch (error) {
        this.refusalCodes = (error.response?.data?.refusals || []).map(r => r.code)
        if (!this.refusalCodes.includes('current_courses_mismatch')) this.allowMismatchedCourses = false
        if (error.response?.data?.errors) {
          this.updateError = error.response.data.errors.join(', ')
        } else {
          this.updateError = error.message || 'Failed to update consumer'
        }
        // Revert Key/Name to their saved values since the update was rejected
        this.form.edit_key = this.initial_values.key
        this.form.edit_name = this.initial_values.name
      }
      this.invalid = false
      this.isUpdating = false
    },
    confirmKeyRename() {
      return new Promise(resolve => {
        this.renameResolver = resolve
        this.showRenameModal = true
      })
    },
    onRenameConfirm() {
      this.showRenameModal = false
      if (this.renameResolver) {
        this.renameResolver(true)
        this.renameResolver = null
      }
    },
    onRenameCancel() {
      this.showRenameModal = false
      if (this.renameResolver) {
        this.renameResolver(false)
        this.renameResolver = null
      }
    },
    confirmWtsChange() {
      return new Promise(resolve => {
        this.wtsResolver = resolve
        this.showWtsModal = true
      })
    },
    onWtsConfirm() {
      this.showWtsModal = false
      if (this.wtsResolver) {
        this.wtsResolver(true)
        this.wtsResolver = null
      }
    },
    onWtsCancel() {
      this.showWtsModal = false
      if (this.wtsResolver) {
        this.wtsResolver(false)
        this.wtsResolver = null
      }
    },
    activationUser(course) {
      if (!course.lti_activations.length) return null
      return course.lti_activations[0].user || null
    },
    activationSource(course) {
      const user = this.activationUser(course)
      if (user) return user.first_name + ' ' + (user.last_name || '').charAt(0) + '.'
      if (course.lti_activations.length) return course.lti_activations[0].source
      return null
    },
    cellValue(course, key) {
      const user = this.activationUser(course)
      const activation = course.lti_activations.length ? course.lti_activations[0] : null
      switch (key) {
        case 'name': return course.context_title || course.context_id
        case 'teacher_first': return user ? user.first_name : ''
        case 'teacher_last': return user ? user.last_name : ''
        case 'email': return user ? user.email : ''
        case 'dates': {
          const created = formatDate(course.created_at)
          if (!activation || !activation.activated_at) return created
          const initials = user ? (user.first_name || '').charAt(0) + (user.last_name || '').charAt(0) : ''
          return created + ' / ' + formatDate(activation.activated_at) + (initials ? ' ' + initials : '')
        }
        case 'join_link': return course.context_id
        case 'course_id': return course.context_id
        case 'canvas_id': return course.canvas_course_id || ''
        case 'domain': return course.canvas_domain || ''
        case 'products': return course.products.map(p => p.product_code).join(', ')
        case 'source': return this.activationSource(course) || ''
        case 'enrollments': return course.processed_count + '/' + course.enrollment_count + (course.counts_unique_emails ? ' *' : '')
        case 'expires': return course.expires_at ? formatDate(course.expires_at) : ''
        default: return ''
      }
    },
    shortDomain(domain) {
      if (!domain) return ''
      return domain.replace('.truewayasl.com', '').replace('.instructure.com', '.inst')
    },
    isExpired(dateStr) {
      if (!dateStr) return false
      return new Date(dateStr) <= new Date()
    },
    truncateId(id) {
      if (!id || id.length <= 12) return id
      return id.slice(0, 5) + '…' + id.slice(-5)
    },
    async copyToClipboard(text, courseId) {
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.copiedId = courseId
      setTimeout(() => { this.copiedId = null }, 1500)
    },
    exportCsv() {
      const cols = this.activeColumns
      const headers = cols.map(c => c.label)
      const csvCell = (val) => {
        const str = String(val || '')
        return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str
      }
      const rows = this.allCourses.map(c =>
        cols.map(col => csvCell(this.cellValue(c, col.key))).join(',')
      )
      const csv = [headers.join(','), ...rows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${this.form.key}-courses.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
    handleKeyInput() {
      if (!this.form.key) {
        // Reset form state when key is cleared
        this.consumer = null;
        this.matches = [];
        this.allCourses = [];
        this.totalCourses = 0;
        this.searchKey = '';
        this.form.name = '';
        this.nameValidation = null;
        this.invalid = false;
      }
    },
  }
}
</script>
